import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosInstance';
import base_url from '../../../constants';
import useNotification from '../../common/Notification';

const SystemHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [unhealthyConfigs, setUnhealthyConfigs] = useState([]);
  const [processStatusData, setProcessStatusData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'unhealthy', 'processes'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const notify = useNotification();

  const fetchHealthOverview = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/health`
      );

      if (response.data && response.data.success) {
        setHealthData(response.data.data);
      } else {
        throw new Error('Failed to fetch health overview');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch system health';
      setError(errorMessage);
      console.error('Error fetching health overview:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnhealthyConfigs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
      });

      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/health/unhealthy?${params}`
      );

      if (response.data && response.data.success) {
        setUnhealthyConfigs(response.data.data.data || []);
        setMeta({
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          total: response.data.data.total,
          last_page: response.data.data.last_page,
        });
      } else {
        throw new Error('Failed to fetch unhealthy configurations');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch unhealthy configurations';
      setError(errorMessage);
      console.error('Error fetching unhealthy configs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  const fetchProcessStatus = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
      });

      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/health/processes?${params}`
      );

      if (response.data && response.data.success) {
        setProcessStatusData(response.data.data.data || []);
        setMeta({
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          total: response.data.data.total,
          last_page: response.data.data.last_page,
        });
      } else {
        throw new Error('Failed to fetch process status');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch process status';
      setError(errorMessage);
      console.error('Error fetching process status:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchHealthOverview();
  }, [fetchHealthOverview]);

  useEffect(() => {
    if (activeTab === 'unhealthy') {
      fetchUnhealthyConfigs();
    } else if (activeTab === 'processes') {
      fetchProcessStatus();
    }
  }, [activeTab, page, rowsPerPage, fetchUnhealthyConfigs, fetchProcessStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main`, fontSize: 48 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !healthData && activeTab === 'overview') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Tabs */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant={activeTab === 'overview' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </Button>
          <Button
            variant={activeTab === 'unhealthy' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('unhealthy')}
          >
            Unhealthy Configurations
          </Button>
          <Button
            variant={activeTab === 'processes' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('processes')}
          >
            Process Status
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={fetchHealthOverview} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && healthData && (
        <Box>
          <Grid container spacing={3}>
            {/* Configurations Overview */}
            <Grid item xs={12} md={4}>
              <StatCard
                title="Total Configurations"
                value={healthData.overview?.total_configs || 0}
                subtitle={`${healthData.overview?.active_configs || 0} active, ${healthData.overview?.inactive_configs || 0} inactive`}
                icon={<TrendingUpIcon />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="Active Configs"
                value={healthData.overview?.active_configs || 0}
                icon={<CheckCircleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="Inactive Configs"
                value={healthData.overview?.inactive_configs || 0}
                icon={<ErrorIcon />}
                color="error"
              />
            </Grid>

            {/* Processes Overview */}
            <Grid item xs={12} md={3}>
              <StatCard
                title="Active Processes"
                value={healthData.processes?.active || 0}
                icon={<CheckCircleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Inactive Processes"
                value={healthData.processes?.inactive || 0}
                icon={<ErrorIcon />}
                color="error"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Healthy Processes"
                value={healthData.processes?.healthy || 0}
                icon={<CheckCircleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Stale Processes"
                value={healthData.processes?.stale || 0}
                icon={<WarningIcon />}
                color="warning"
              />
            </Grid>

            {/* Validation Overview */}
            <Grid item xs={12} md={6}>
              <StatCard
                title="Valid Configurations"
                value={healthData.validation?.valid || 0}
                icon={<CheckCircleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StatCard
                title="Invalid Configurations"
                value={healthData.validation?.invalid || 0}
                icon={<ErrorIcon />}
                color="error"
              />
            </Grid>

            {/* Automation Overview */}
            <Grid item xs={12} md={3}>
              <StatCard
                title="Automation Enabled"
                value={healthData.automation?.enabled || 0}
                icon={<CheckCircleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Automation Disabled"
                value={healthData.automation?.disabled || 0}
                icon={<ErrorIcon />}
                color="error"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Recent Failures"
                value={healthData.automation?.recent_failures || 0}
                icon={<WarningIcon />}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="High Failure Count"
                value={healthData.automation?.high_failure_count || 0}
                icon={<ErrorIcon />}
                color="error"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Unhealthy Configurations Tab */}
      {activeTab === 'unhealthy' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Process Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Is Valid</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Failure Count</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Run Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Failure Reason</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Heartbeat</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : unhealthyConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">No unhealthy configurations found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                unhealthyConfigs.map((config) => (
                  <TableRow key={config.id} hover>
                    <TableCell>{config.user_id}</TableCell>
                    <TableCell>{config.user?.name || '-'}</TableCell>
                    <TableCell>{config.user?.email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.status}
                        size="small"
                        color={config.status === 'ACTIVE' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.process_status}
                        size="small"
                        color={config.process_status === 'ACTIVE' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.is_valid ? 'Yes' : 'No'}
                        size="small"
                        color={config.is_valid ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{config.failure_count || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.last_run_status || '-'}
                        size="small"
                        color={config.last_run_status === 'SUCCESS' ? 'success' : config.last_run_status === 'FAILED' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{config.last_failure_reason || '-'}</TableCell>
                    <TableCell>{formatDate(config.last_heartbeat)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={meta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 15, 25, 50]}
          />
        </TableContainer>
      )}

      {/* Process Status Tab */}
      {activeTab === 'processes' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Process Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Is Valid</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Automation Enabled</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Run Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Failure Count</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Is Healthy</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Heartbeat Age (min)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Heartbeat</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : processStatusData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">No process status data found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                processStatusData.map((config) => (
                  <TableRow key={config.id} hover>
                    <TableCell>{config.user_id}</TableCell>
                    <TableCell>{config.user?.name || '-'}</TableCell>
                    <TableCell>{config.pid || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.process_status}
                        size="small"
                        color={config.process_status === 'ACTIVE' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.is_valid ? 'Yes' : 'No'}
                        size="small"
                        color={config.is_valid ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.automation_enabled ? 'Yes' : 'No'}
                        size="small"
                        color={config.automation_enabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.last_run_status || '-'}
                        size="small"
                        color={config.last_run_status === 'SUCCESS' ? 'success' : config.last_run_status === 'FAILED' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{config.failure_count || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.is_healthy ? 'Yes' : 'No'}
                        size="small"
                        color={config.is_healthy ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{config.heartbeat_age_minutes || 0}</TableCell>
                    <TableCell>{formatDate(config.last_heartbeat)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={meta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 15, 25, 50]}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default SystemHealth;


